/**
 * MSAL bootstrap — used only when NOT running inside a Teams host.
 *
 * This is the dev-mode / browser fallback auth path. In prod the Teams SDK
 * `authentication.getAuthToken()` is preferred (SSO, no popup).
 */

import {
  PublicClientApplication,
  LogLevel,
  type Configuration,
} from '@azure/msal-browser';

const clientId = (import.meta.env.VITE_AAD_CLIENT_ID as string | undefined) ?? '';
const tenantId = (import.meta.env.VITE_AAD_TENANT_ID as string | undefined) ?? 'common';

export const MSAL_CONFIG: Configuration = {
  auth: {
    clientId: clientId || '00000000-0000-0000-0000-000000000000',
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: typeof window !== 'undefined' ? window.location.origin : '/',
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message) => {
        if (level <= LogLevel.Warning) {
          // eslint-disable-next-line no-console
          console.warn('[msal]', message);
        }
      },
    },
  },
};

/** Default scopes for Function app + Graph presence reads. */
export const DEFAULT_SCOPES = ['User.Read', 'openid', 'profile', 'email'];

export const msalInstance = new PublicClientApplication(MSAL_CONFIG);

/**
 * Single-flight initialize + redirect handler. Every entry point into MSAL
 * must await this before calling any other MSAL API so the library's internal
 * state transitions cleanly (initialize + clear any `msal.*.interaction.status`
 * left behind by a prior redirect round-trip). Re-entry is a no-op — the
 * returned promise is cached.
 */
let msalReadyPromise: Promise<void> | null = null;
export function ensureMsalReady(): Promise<void> {
  if (!msalReadyPromise) {
    msalReadyPromise = (async () => {
      await msalInstance.initialize();
      // handleRedirectPromise MUST be called on every page load to finalize an
      // in-flight redirect auth and clear sessionStorage's interaction flag.
      // Without it, a subsequent acquireTokenPopup/Redirect throws
      // "interaction_in_progress" indefinitely.
      await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    })();
  }
  return msalReadyPromise;
}

/**
 * Acquire an access token. Order:
 *   1. ensureMsalReady (initialize + handleRedirectPromise)
 *   2. If we have an account, try acquireTokenSilent.
 *   3. On silent failure / no account, show `loginPopup` — keeps the SPA
 *      alive (no navigation away) and is the recommended flow for modern
 *      SPAs hosted outside an iframe.
 *
 * `loginPopup` vs `acquireTokenRedirect`: popup preserves all in-memory state
 * (ConfigContext, routes, etc.). Redirect dumps the page, forces us to
 * rebuild state after return, and was the source of the
 * "interaction_in_progress" bug we saw in production.
 */
export async function getMsalToken(scopes: string[] = DEFAULT_SCOPES): Promise<string> {
  await ensureMsalReady();

  const accounts = msalInstance.getAllAccounts();
  const firstAccount = accounts[0];

  if (firstAccount) {
    try {
      const result = await msalInstance.acquireTokenSilent({
        scopes,
        account: firstAccount,
      });
      return result.accessToken;
    } catch {
      // Silent failed — interactive fallback below.
    }
  }

  // No account OR silent acquisition failed → interactive popup.
  const popup = await msalInstance.loginPopup({ scopes });
  return popup.accessToken;
}

/**
 * Browser-direct SILENT auth probe (Bill 2026-06-03). Determines whether the
 * person hitting the SWA in a plain browser has an existing Microsoft session —
 * WITHOUT any interactive popup. Used to split the browser landing:
 *   true  → authenticated → "open in Teams" page
 *   false → not authenticated → generic "Not Authorized" page (no app info)
 *
 * Uses a cached account's silent token if present, else `ssoSilent` (hidden
 * iframe, prompt=none against the active Entra session). Both are iframe-based,
 * so COOP / popup-blocking never applies — this is what avoids the Edge 148
 * `monitorPopupForHash - window closed` failure. Fails CLOSED: any error (incl.
 * interaction_required / no session) returns false so unauthenticated visitors
 * see the generic gate and the app's purpose is never revealed.
 */
export async function tryBrowserSsoSilent(scopes: string[] = DEFAULT_SCOPES): Promise<boolean> {
  try {
    await ensureMsalReady();
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const r = await msalInstance.acquireTokenSilent({ scopes, account: accounts[0] });
      return !!r.accessToken;
    }
    const r = await msalInstance.ssoSilent({ scopes });
    return !!r.accessToken;
  } catch (e) {
    // Bill 2026-06-03: distinguish "a Microsoft session EXISTS but can't be
    // resolved silently" from "no session at all". `interaction_required` /
    // `consent_required` / AADSTS16000 (multiple user identities) all mean the
    // person IS signed in — silent just can't pick/finish without a prompt — so
    // they're AUTHENTICATED → route to Teams. Only a genuine no-session
    // (login_required / AADSTS50058 / no_tokens_found) is unauthenticated.
    const err = e as { errorCode?: string; message?: string };
    const code = (err?.errorCode ?? '').toLowerCase();
    const msg = (err?.message ?? String(e ?? '')).toLowerCase();
    const hasSession =
      code === 'interaction_required' ||
      code === 'consent_required' ||
      msg.includes('aadsts16000') ||
      msg.includes('interaction_required') ||
      msg.includes('multiple user identities') ||
      msg.includes('consent_required');
    return hasSession; // session-exists → authenticated; else fail closed
  }
}
